import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@web-app/components/ui/Collapsible/collapsible';
import { cn } from '@web-app/lib/utils';
import { FC, useEffect, useRef, useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

import { ArchivedGoalsTable } from './ArchivedGoalsTable';
import { NewGoalInput } from './NewGoalInput';
import { SessionGoalItem } from './SessionGoalItem';
import type { NewGoalInputRef, SessionGoalsUIProps } from './types';

const VISIBILITY_MESSAGE = 'These goals are visible to the patient.';
const CARD_DESCRIPTION_MAIN =
  'During your session, set 1\u20133 actionable goals ';
const CARD_DESCRIPTION_NOTE = '(goals are visible to patients)';

// ---------------------------------------------------------------------------
// GoalsHeader
// ---------------------------------------------------------------------------

interface GoalsHeaderProps {
  goalCount: number;
  maxGoals: number;
  variant: 'card' | 'default';
  stepNumber?: number;
}

const GoalsHeader: FC<GoalsHeaderProps> = ({
  goalCount,
  maxGoals,
  variant,
  stepNumber,
}) => {
  if (variant === 'card') {
    const label = stepNumber
      ? `Step ${stepNumber}: Set Patient Goals`
      : 'Set Patient Goals';

    return (
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          <span className="ml-auto text-xs text-muted-foreground">
            {goalCount}/{maxGoals}
          </span>
        </div>
        <p className="mt-1 text-xs">
          <span className="text-foreground">{CARD_DESCRIPTION_MAIN}</span>
          <span className="text-muted-foreground">{CARD_DESCRIPTION_NOTE}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-1">
      <span className="font-semibold text-foreground">
        Patient Goals ({goalCount}/{maxGoals})
      </span>
      <span className="text-sm italic text-muted-foreground">
        {VISIBILITY_MESSAGE}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton (replaces Chakra SessionPrepCardSkeleton for this component)
// ---------------------------------------------------------------------------

function GoalsSkeleton() {
  return (
    <div data-testid="session-goals-skeleton" className="flex flex-col gap-3">
      <div className="h-3 w-40 animate-pulse rounded bg-muted" />
      <div className="h-8 w-full animate-pulse rounded bg-muted" />
      <div className="h-8 w-full animate-pulse rounded bg-muted" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionGoalsUI
// ---------------------------------------------------------------------------

export const SessionGoalsUI = ({
  isLoading,
  goals,
  archivedGoals,
  maxGoals,
  isMutating,
  onCreateGoal,
  onArchiveGoal,
  onUpdateGoal,
  showArchived = true,
  variant = 'default',
  stepNumber,
}: SessionGoalsUIProps) => {
  const goalCount = goals.length;
  const canAddMore = goalCount < maxGoals;
  const inputRef = useRef<NewGoalInputRef>(null);
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  const isCardVariant = variant === 'card';

  const handleCreateGoal = (content: string) => {
    setShouldFocusInput(true);
    onCreateGoal(content);
  };

  const handleArchiveGoal = (goalId: string) => {
    setShouldFocusInput(true);
    onArchiveGoal(goalId);
  };

  // Focus the new goal input after adding/archiving a goal, but not on initial load.
  useEffect(
    function focusInputAfterGoalChange() {
      if (shouldFocusInput && goals.length < maxGoals) {
        inputRef.current?.focus();
      }
    },
    [shouldFocusInput, goals.length, maxGoals],
  );

  if (isLoading) {
    return <GoalsSkeleton />;
  }

  return (
    <>
      <div
        data-testid="session-goals"
        className={cn(
          'rounded-sm border',
          isCardVariant
            ? 'border-transparent bg-transparent p-0'
            : 'border-border bg-background p-4',
        )}
      >
        <GoalsHeader
          goalCount={goalCount}
          maxGoals={maxGoals}
          variant={variant}
          stepNumber={stepNumber}
        />

        <div className="flex flex-col gap-3">
          {goals.map((goal) => (
            <SessionGoalItem
              key={goal.id}
              goal={goal}
              onArchive={() => handleArchiveGoal(goal.id)}
              onUpdate={(content) => onUpdateGoal(goal.id, content)}
              isDisabled={isMutating}
            />
          ))}

          {canAddMore && (
            <NewGoalInput
              ref={inputRef}
              onSubmit={handleCreateGoal}
              placeholder="Enter a SMART goal..."
              isDisabled={isMutating}
            />
          )}
        </div>
      </div>

      {showArchived && (
        <Collapsible
          open={isArchivedOpen}
          onOpenChange={setIsArchivedOpen}
          className="mt-3"
          data-testid="archived-goals-section"
        >
          <CollapsibleTrigger
            className="flex cursor-pointer items-center gap-1 py-1 hover:opacity-80"
            data-testid="archived-goals-toggle"
          >
            {isArchivedOpen ? (
              <LuChevronDown className="size-4 text-muted-foreground" />
            ) : (
              <LuChevronRight className="size-4 text-muted-foreground" />
            )}
            <span className="text-sm text-muted-foreground">
              Archived Goals
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {isArchivedOpen && (
              <div className="mt-2">
                <ArchivedGoalsTable
                  goals={archivedGoals}
                  isLoading={isLoading}
                />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  );
};
